import { createAsyncThunk, createSlice, createSelector } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { getData } from 'features/dinamic/dinamicApi';
import { setLoading } from 'features/loader';
import { setDataToast, setVisibleToast } from 'features/toast';
import AlertInterface from 'features/toast/toastInterface';

interface EvaluacionItem {
  idEvaluacion: number;
  idCiclo: number;
  idModulo: number | null;
  nombre: string;
  nombreCiclo: string | null;
  nombreModulo: string | null;
  contextoTipo: 'ciclo' | 'modulo' | 'sesion' | 'general';
  asignacionTipo: 'ciclo' | 'modulo_direct' | 'modulo_rel' | 'sesion' | 'general';
  contextoDescripcion: string;
  modulosAsignados: Array<{ idModulo: number; nombre: string }>;
  sesionNombre?: string | null;
  sesionFecha?: string | null;
  fechaInicio: string;
  fechaVencimiento: string;
  mensajeInicial: string;
  mensajeFinal: string;
  creditos: number | null;
  esEncuesta: boolean;
  diasRestantes: number;
}

interface IntentoItem {
  idUsuario: number;
  nombreAlumno: string;
  carnet: string;
  idEvaluacionIntento: number;
  estado: string;
  fechaInicioIntento: string;
  fechaFinIntento: string;
  puntajeObtenido: number | null;
  porcentaje: number | null;
  creditos: number | null;
  fechaVencimiento: string;
}

interface RespuestaItem {
  idPregunta: number;
  orden: number;
  titulo: string;
  descripcion: string;
  punteo: number | null;
  punteoMinimo: number | null;
  tipoPregunta: string;
  idEvaluacionRespuesta: number;
  idRespuesta: number;
  respuestaAbierta: string;
  puntajeObtenido: number | null;
  esCorrecta: boolean | null;
  retroalimentacion: string;
  respondida: boolean;
  fechaRespuesta: string;
  urlReferencia?: string | null;
  respuestaCorrectaTexto: string | null;
  respuestaCorrectaId: number | null;
  opciones: Array<{
    idRespuesta: number;
    descripcion: string;
    valido: boolean;
  }>;
}

interface EncuestaResumenPreguntaOpcion {
  idRespuesta: number | null;
  descripcion: string | null;
  totalSeleccionada: number;
  porcentaje: number | null;
}

interface EncuestaResumenPregunta {
  idPregunta: number;
  idTipoPregunta: number;
  orden: number;
  titulo: string;
  tipoPregunta: string;
  totalAsignados: number;
  totalRespondidas: number;
  totalSinResponder: number;
  opciones: EncuestaResumenPreguntaOpcion[] | null;
  respuestasAbiertas: Array<{ respuesta: string | null; total: number; porcentaje: number | null }> | null;
}

interface EncuestaResumen {
  idEvaluacion: number;
  nombre: string;
  fechaInicio: string;
  fechaVencimiento: string;
  totalAsignados: number;
  totalCompletadas: number;
  totalPendientes: number;
  totalEnProgreso: number;
  porcentajeParticipacion: number | null;
  preguntas: EncuestaResumenPregunta[];
}

interface FacilitadorEvaluacionState {
  status: string;
  error: string;
  evaluacionList: EvaluacionItem[];
  intentoList: IntentoItem[];
  respuestaList: RespuestaItem[];
  evaluacionSelected: EvaluacionItem | null;
  intentoSelected: IntentoItem | null;
  encuestaResumen: EncuestaResumen | null;
  encuestaResumenStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  filtros: {
    idCiclo: number | null;
    idModulo: number | null;
    idGrupo: number | null;
  };
  lastQueryKey: string | null;
  currentQueryKey: string | null;
}

const initialState: FacilitadorEvaluacionState = {
  status: 'idle',
  error: '',
  evaluacionList: [],
  intentoList: [],
  respuestaList: [],
  evaluacionSelected: null,
  intentoSelected: null,
  encuestaResumen: null,
  encuestaResumenStatus: 'idle',
  filtros: {
    idCiclo: null,
    idModulo: null,
    idGrupo: null,
  },
  lastQueryKey: null,
  currentQueryKey: null,
};

const normalizeEvaluaciones = (items: any[]): EvaluacionItem[] =>
  items.map((item: any) => ({
    ...item,
    contextoTipo: item.contextoTipo ?? 'general',
    asignacionTipo: item.asignacionTipo ?? 'general',
    contextoDescripcion: item.contextoDescripcion ?? '',
    modulosAsignados: Array.isArray(item.modulosAsignados) ? item.modulosAsignados : [],
    sesionNombre: item.sesionNombre ?? null,
    sesionFecha: item.sesionFecha ?? null,
  }));

export const fetchEvaluacionList = createAsyncThunk(
  'facilitadorEvaluacion/fetchList',
  async (params: any, { dispatch, getState }) => {
    dispatch(setLoading(true));
    const { result, message, data } = await getData(
      { idsp: 137, ...params },
      getState,
      dispatch
    );
    dispatch(setLoading(false));

    if (result) {
      // Manejar caso cuando data es null pero result es true
      if (data === null || data === undefined) {
        return [];
      }
      // Asegurar que siempre sea array
      return Array.isArray(data) ? data : [data];
    } else {
      const alert: AlertInterface = {
        title: 'ERROR',
        message: message || 'Error al obtener evaluaciones',
        data: [],
        type: 'Crimson',
        time: Date.now()
      };
      dispatch(setDataToast(alert));
      dispatch(setVisibleToast(true));
      setTimeout(() => {
        dispatch(setVisibleToast(false));
      }, 4000);
      return [];
    }
  }
);

export const fetchIntentosByEvaluacion = createAsyncThunk(
  'facilitadorEvaluacion/fetchIntentos',
  async (params: { idEvaluacion: number; idGrupo?: number }, { dispatch, getState }) => {
    dispatch(setLoading(true));
    const { result, message, data } = await getData(
      { idsp: 138, ...params },
      getState,
      dispatch
    );
    dispatch(setLoading(false));

    if (result) {
      if (data === null || data === undefined) {
        return [];
      }
      return Array.isArray(data) ? data : [data];
    } else {
      const alert: AlertInterface = {
        title: 'ERROR',
        message: message || 'Error al obtener intentos',
        data: [],
        type: 'Crimson',
        time: Date.now()
      };
      dispatch(setDataToast(alert));
      dispatch(setVisibleToast(true));
      setTimeout(() => {
        dispatch(setVisibleToast(false));
      }, 4000);
      return [];
    }
  }
);

export const fetchRespuestasByIntento = createAsyncThunk(
  'facilitadorEvaluacion/fetchRespuestas',
  async (params: { idEvaluacionIntento: number }, { dispatch, getState }) => {
    dispatch(setLoading(true));
    const { result, message, data } = await getData(
      { idsp: 139, ...params },
      getState,
      dispatch
    );
    dispatch(setLoading(false));

    if (result) {
      if (data === null || data === undefined) {
        return [];
      }
      return Array.isArray(data) ? data : [data];
    } else {
      const alert: AlertInterface = {
        title: 'ERROR',
        message: message || 'Error al obtener respuestas',
        data: [],
        type: 'Crimson',
        time: Date.now()
      };
      dispatch(setDataToast(alert));
      dispatch(setVisibleToast(true));
      setTimeout(() => {
        dispatch(setVisibleToast(false));
      }, 4000);
      return [];
    }
  }
);

export const fetchEncuestaResumen = createAsyncThunk(
  'facilitadorEvaluacion/fetchEncuestaResumen',
  async (params: { idEvaluacion: number; idGrupo?: number }, { dispatch, getState }) => {
    dispatch(setLoading(true));
    const { result, message, data } = await getData(
      { idsp: 141, ...params },
      getState,
      dispatch
    );
    dispatch(setLoading(false));

    if (result) {
      if (data === null || data === undefined) {
        return null;
      }
      return data;
    } else {
      const alert: AlertInterface = {
        title: 'ERROR',
        message: message || 'Error al obtener resumen de encuesta',
        data: [],
        type: 'Crimson',
        time: Date.now()
      };
      dispatch(setDataToast(alert));
      dispatch(setVisibleToast(true));
      setTimeout(() => {
        dispatch(setVisibleToast(false));
      }, 4000);
      throw new Error(message);
    }
  }
);

export const calificarRespuestaAbierta = createAsyncThunk(
  'facilitadorEvaluacion/calificarRespuesta',
  async (params: {
    idEvaluacionRespuesta: number;
    puntajeObtenido: number;
    retroalimentacion: string;
  }, { dispatch, getState }) => {
    dispatch(setLoading(true));
    const { result, message } = await getData(
      { idsp: 140, ...params },
      getState,
      dispatch
    );
    dispatch(setLoading(false));

    if (result) {
      const alertSuccess: AlertInterface = {
        title: 'ÉXITO',
        message: message || 'Calificación guardada correctamente',
        data: [],
        type: 'LimeGreen',
        time: Date.now()
      };
      dispatch(setDataToast(alertSuccess));
      dispatch(setVisibleToast(true));
      setTimeout(() => {
        dispatch(setVisibleToast(false));
      }, 4000);
      return true;
    } else {
      const alertError: AlertInterface = {
        title: 'ERROR',
        message: message || 'Error al calificar respuesta',
        data: [],
        type: 'Crimson',
        time: Date.now()
      };
      dispatch(setDataToast(alertError));
      dispatch(setVisibleToast(true));
      setTimeout(() => {
        dispatch(setVisibleToast(false));
      }, 4000);
      return false;
    }
  }
);

export const facilitadorEvaluacionSlice = createSlice({
  name: 'facilitadorEvaluacion',
  initialState,
  reducers: {
    setEvaluacionSelected: (state, action) => {
      state.evaluacionSelected = action.payload;
      state.encuestaResumen = null;
      state.encuestaResumenStatus = 'idle';
    },
    setIntentoSelected: (state, action) => {
      state.intentoSelected = action.payload;
    },
    clearEvaluacionSelected: (state) => {
      state.evaluacionSelected = null;
      state.intentoList = [];
      state.respuestaList = [];
      state.encuestaResumen = null;
      state.encuestaResumenStatus = 'idle';
    },
    clearIntentoSelected: (state) => {
      state.intentoSelected = null;
      state.respuestaList = [];
    },
    setFiltros: (state, action) => {
      state.filtros = { ...state.filtros, ...action.payload };
    },
    clearFiltros: (state) => {
      state.filtros = {
        idCiclo: null,
        idModulo: null,
        idGrupo: null,
      };
    },
    clearEncuestaResumen: (state) => {
      state.encuestaResumen = null;
      state.encuestaResumenStatus = 'idle';
    },
    invalidateEvaluacionCache: (state) => {
      state.status = 'idle';
      state.lastQueryKey = null;
      state.currentQueryKey = null;
    },
    clearEvaluacionList: (state) => {
      state.evaluacionList = [];
      state.status = 'idle';
      state.lastQueryKey = null;
      state.currentQueryKey = null;
    },
    clearFacilitadorEvaluacion: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEvaluacionList.pending, (state, action) => {
        state.status = 'loading';
        state.currentQueryKey = JSON.stringify(action.meta.arg ?? {}) ?? '{}';
      })
      .addCase(fetchEvaluacionList.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.evaluacionList = normalizeEvaluaciones(action.payload ?? []);
        state.lastQueryKey = state.currentQueryKey ?? state.lastQueryKey;
        state.currentQueryKey = null;
      })
      .addCase(fetchEvaluacionList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Error al cargar evaluaciones';
        state.lastQueryKey = state.currentQueryKey ?? state.lastQueryKey;
        state.currentQueryKey = null;
      })
      .addCase(fetchIntentosByEvaluacion.fulfilled, (state, action) => {
        state.intentoList = action.payload;
      })
      .addCase(fetchRespuestasByIntento.fulfilled, (state, action) => {
        state.respuestaList = action.payload;
      })
      .addCase(fetchEncuestaResumen.pending, (state) => {
        state.encuestaResumenStatus = 'loading';
      })
      .addCase(fetchEncuestaResumen.fulfilled, (state, action) => {
        state.encuestaResumenStatus = 'succeeded';
        state.encuestaResumen = action.payload;
      })
      .addCase(fetchEncuestaResumen.rejected, (state, action) => {
        state.encuestaResumenStatus = 'failed';
        state.error = action.error.message || 'Error al obtener resumen de encuesta';
      });
  },
});

export const {
  setEvaluacionSelected,
  setIntentoSelected,
  clearEvaluacionSelected,
  clearIntentoSelected,
  setFiltros,
  clearFiltros,
  clearEncuestaResumen,
  invalidateEvaluacionCache,
  clearEvaluacionList,
  clearFacilitadorEvaluacion,
} = facilitadorEvaluacionSlice.actions;

export const selectEvaluacionList = (state: RootState) => state.facilitadorEvaluacion.evaluacionList;
export const selectEvaluacionesAcademicas = createSelector([
  selectEvaluacionList,
], (evaluaciones) => evaluaciones.filter((item) => !item.esEncuesta));
export const selectEncuestaList = createSelector([
  selectEvaluacionList,
], (evaluaciones) => evaluaciones.filter((item) => item.esEncuesta));
export const selectIntentoList = (state: RootState) => state.facilitadorEvaluacion.intentoList;
export const selectRespuestaList = (state: RootState) => state.facilitadorEvaluacion.respuestaList;
export const selectEvaluacionSelected = (state: RootState) => state.facilitadorEvaluacion.evaluacionSelected;
export const selectIntentoSelected = (state: RootState) => state.facilitadorEvaluacion.intentoSelected;
export const selectEncuestaResumen = (state: RootState) => state.facilitadorEvaluacion.encuestaResumen;
export const selectEncuestaResumenStatus = (state: RootState) => state.facilitadorEvaluacion.encuestaResumenStatus;
export const selectFiltros = (state: RootState) => state.facilitadorEvaluacion.filtros;
export const selectStatus = (state: RootState) => state.facilitadorEvaluacion.status;
export const selectLastQueryKey = (state: RootState) => state.facilitadorEvaluacion.lastQueryKey;
export const selectCurrentQueryKey = (state: RootState) => state.facilitadorEvaluacion.currentQueryKey;
export const selectIntentoStats = createSelector([
  selectIntentoList,
], (intentos) => {
  const stats = {
    total: intentos.length,
    pendientes: 0,
    enProgreso: 0,
    finalizadas: 0,
    expiradas: 0,
  };

  intentos.forEach((intento) => {
    if (!intento.idEvaluacionIntento) {
      stats.pendientes += 1;
    }

    if (intento.estado === 'en_progreso') {
      stats.enProgreso += 1;
    }

    if (intento.estado === 'finalizada') {
      stats.finalizadas += 1;
    }

    if (intento.estado === 'expirada') {
      stats.expiradas += 1;
    }
  });

  return stats;
});

export default facilitadorEvaluacionSlice.reducer;
